"use client";
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const PURPLE_LIGHT = '#a5b4fc';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function HeartIcon({ size = 14, color = 'rgba(8,4,0,0.55)' }: { size?: number; color?: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M12 20.5s-7.5-4.6-9.8-9C.7 8 2 4.5 5.5 3.8c2-.4 3.7.5 4.9 2.2h1.2c1.2-1.7 2.9-2.6 4.9-2.2C20 4.5 21.3 8 19.8 11.5c-2.3 4.4-9.8 9-9.8 9z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    );
}
function CommentIcon({ size = 14, color = 'rgba(8,4,0,0.55)' }: { size?: number; color?: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M3 12a8 8 0 1 1 3.6 6.7L3 20l1.3-3.5A7.96 7.96 0 0 1 3 12z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    );
}
function ShareIcon({ size = 14, color = 'rgba(8,4,0,0.55)' }: { size?: number; color?: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M4 12l16-7-6 16-2.5-6.5L4 12z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    );
}

interface Post {
    user: string;
    body: string;
    avatar: string;
    sponsored?: boolean;
}

const posts: Post[] = [
    { user: 'wanderlust.co', body: 'New arrivals just dropped', avatar: PURPLE },
    { user: 'foodie.diaries', body: 'This recipe broke the internet', avatar: INK },
    { user: 'yourbrand', body: 'Stop the scroll. Start the conversation.', avatar: LIME, sponsored: true },
    { user: 'fitlife.hq', body: '5 exercises for better posture', avatar: PURPLE },
    { user: 'travel.stories', body: 'Hidden gems in the mountains', avatar: INK },
    { user: 'dailydose', body: 'Monday motivation, delivered', avatar: PURPLE },
];

const POST_HEIGHT = 142;
const SET_HEIGHT = POST_HEIGHT * posts.length;

function FeedPost({ post }: { post: Post }) {
    return (
        <div
            className="flex flex-col gap-2 px-4 py-3"
            style={{
                height: POST_HEIGHT,
                background: post.sponsored ? 'rgba(55,48,163,0.04)' : 'transparent',
                borderTop: '1px solid rgba(8,4,0,0.06)',
                borderLeft: post.sponsored ? `2.5px solid ${PURPLE}` : '2.5px solid transparent',
            }}
        >
            <div className="flex items-center gap-2">
                <span
                    className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold"
                    style={{ background: post.avatar, color: post.avatar === LIME ? INK : '#fff', fontFamily: CLASH }}
                >
                    {post.user.charAt(0).toUpperCase()}
                </span>
                <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-[11px] font-bold truncate" style={{ fontFamily: MANROPE, color: INK }}>
                        {post.user}
                    </span>
                    {post.sponsored && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ fontFamily: MANROPE, color: PURPLE }}>
                            Sponsored
                        </span>
                    )}
                </div>
            </div>
            <p className="text-[11px] leading-snug" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.7)' }}>
                {post.body}
            </p>
            <div
                className="flex-1 rounded-lg min-h-[38px]"
                style={{ background: post.sponsored ? 'rgba(206,255,26,0.25)' : 'rgba(8,4,0,0.045)' }}
            />
            <div className="flex items-center gap-3 pt-0.5">
                <HeartIcon color={post.sponsored ? PURPLE : undefined} />
                <CommentIcon color={post.sponsored ? PURPLE : undefined} />
                <ShareIcon color={post.sponsored ? PURPLE : undefined} />
            </div>
        </div>
    );
}

function ReactionBurst() {
    const bursts = [
        { Icon: HeartIcon, x: -18, delay: 0 },
        { Icon: CommentIcon, x: 0, delay: 0.15 },
        { Icon: ShareIcon, x: 18, delay: 0.3 },
    ];
    return (
        <motion.div
            className="absolute left-1/2 z-20 flex items-center gap-1 pointer-events-none"
            style={{ top: '46%' }}
            animate={{ opacity: [0, 0, 1, 1, 0], y: [0, 0, -6, -6, -22] }}
            transition={{ duration: 10, times: [0, 0.28, 0.34, 0.56, 0.66], repeat: Infinity, ease: 'easeOut' }}
        >
            {bursts.map(({ Icon, x }, i) => (
                <span
                    key={i}
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: LIME, marginLeft: i === 0 ? 0 : -4, transform: `translateX(${x}px)`, boxShadow: '0 6px 16px -6px rgba(8,4,0,0.4)' }}
                >
                    <Icon size={13} color={INK} />
                </span>
            ))}
        </motion.div>
    );
}

function ScrollStopMockup() {
    return (
        <div className="relative w-full max-w-[300px] mx-auto">
            {/* ambient glow behind the phone */}
            <div
                className="absolute pointer-events-none"
                style={{
                    left: '50%', top: '50%', width: 380, height: 460, marginLeft: -190, marginTop: -230,
                    background: 'radial-gradient(circle, rgba(55,48,163,0.35) 0%, transparent 70%)',
                }}
            />

            {/* phone frame */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.25, ease: 'easeOut' }}
                className="relative rounded-[42px] p-2.5"
                style={{ background: INK, boxShadow: '0 30px 70px -20px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.06)' }}
            >
                <div className="absolute left-1/2 top-2.5 -translate-x-1/2 w-16 h-4 rounded-full" style={{ background: '#000' }} />

                <div className="relative rounded-[32px] overflow-hidden" style={{ background: CARD, height: 500 }}>
                    {/* status row */}
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <span className="text-[10px] font-bold" style={{ fontFamily: MANROPE, color: INK }}>9:41</span>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                            <span className="w-3 h-3 rounded-full" style={{ background: 'rgba(8,4,0,0.15)' }} />
                        </div>
                    </div>

                    {/* scrolling feed viewport */}
                    <div className="relative overflow-hidden" style={{ height: 440 }}>
                        <motion.div
                            animate={{ y: [0, -SET_HEIGHT * 0.42, -SET_HEIGHT * 0.42, -SET_HEIGHT] }}
                            transition={{ duration: 10, times: [0, 0.3, 0.62, 1], repeat: Infinity, ease: ['easeInOut', 'linear', 'easeInOut'] }}
                        >
                            {[...posts, ...posts].map((p, i) => (
                                <FeedPost key={i} post={p} />
                            ))}
                        </motion.div>

                        <ReactionBurst />

                        {/* edge fades */}
                        <div className="absolute top-0 left-0 right-0 h-6 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${CARD}, transparent)` }} />
                        <div className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none" style={{ background: `linear-gradient(to top, ${CARD}, transparent)` }} />
                    </div>
                </div>
            </motion.div>

            {/* live badge */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.9, ease: 'backOut' }}
                className="absolute -right-2 top-16 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: CARD, boxShadow: '0 10px 24px -10px rgba(8,4,0,0.35)' }}
            >
                <motion.span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: PURPLE }}
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                />
                <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: 'rgba(8,4,0,0.5)' }}>
                    scroll-stopping
                </span>
            </motion.div>
        </div>
    );
}

const tools = [
    { src: '/logos/meta.svg', alt: 'Meta Ads' },
    { src: '/logos/analytics.svg', alt: 'Google Analytics' },
];

export default function MetaAdsHero() {
    const { openModal } = useContactModal();

    return (
        <section
            className="relative overflow-hidden"
            style={{ background: INK, borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
        >
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />
            <div
                className="absolute pointer-events-none"
                style={{
                    top: '-16%', left: '-8%', width: 560, height: 520,
                    background: 'radial-gradient(circle, rgba(55,48,163,0.35) 0%, transparent 68%)',
                    borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                }}
            />

            <div className="relative max-w-[1360px] mx-auto px-6 md:px-16 lg:px-24 pt-36 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

                <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative order-2 lg:order-1">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center gap-2 text-xs md:text-sm font-medium"
                        style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}
                    >
                        <Link href="/services/performance-marketing" className="hover:underline" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Performance Marketing
                        </Link>
                        <span>/</span>
                        <span style={{ color: LIME }}>Meta Ads Management</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] tracking-tight"
                        style={{ fontFamily: CLASH, color: '#ffffff' }}
                    >
                        Ads Built to{' '}
                        <span style={{ color: LIME }}>Stop the Scroll</span>
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.22 }}
                        className="flex flex-col gap-4 max-w-xl"
                    >
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.01em' }}>
                            People don&apos;t always open Instagram or Facebook looking to buy. That&apos;s precisely what makes Meta advertising different.
                        </p>
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.01em' }}>
                            Lead Workz combines audience psychology, creative strategy, campaign optimisation and conversion data to turn attention into meaningful business outcomes.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.32 }}
                        className="pt-1"
                    >
                        <button
                            onClick={openModal}
                            className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                            style={{ background: LIME, color: INK, fontFamily: CLASH, letterSpacing: '0.08em' }}
                        >
                            Build My Meta Ads Strategy →
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                        className="flex items-center gap-4 pt-3"
                    >
                        <span className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.35)' }}>
                            Managed with
                        </span>
                        <div className="flex items-center gap-3">
                            {tools.map((t) => (
                                <div
                                    key={t.alt}
                                    className="w-8 h-8 rounded-lg flex items-center justify-center p-1.5 opacity-60 hover:opacity-100 transition-opacity duration-300"
                                    style={{ background: 'rgba(255,255,255,0.06)' }}
                                >
                                    <Image src={t.src} alt={t.alt} width={24} height={24} className="w-full h-full object-contain" />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                <div className="lg:col-span-5 order-1 lg:order-2">
                    <ScrollStopMockup />
                </div>

            </div>
        </section>
    );
}
